/**
 * Equipe — consultores e o desempenho de cada um na competência.
 *
 * O vínculo com o SGA aparece explícito: consultor sem `codigoVoluntario` não
 * tem como receber placa atribuída, e isso precisa ser visível em vez de virar
 * um zero silencioso no ranking.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Users, AlertTriangle, Link2Off } from 'lucide-react';
import { api } from '../lib/api.ts';
import { competencia, competenciaAtual, iniciais, numero } from '../lib/format.ts';
import { EstadoVazio, Esqueleto, Card } from '../components/ui/index.tsx';

interface MembroEquipe {
  id: string;
  nome: string;
  papel: string;
  regional: string | null;
  codigoVoluntario: string | null;
  placasLiquidas: number;
  metaPlacas: number | null;
}

export default function Equipe() {
  const [comp] = useState(competenciaAtual());

  const { data, isLoading } = useQuery({
    queryKey: ['equipe', comp],
    queryFn: () => api.get<{ membros: MembroEquipe[] }>(`/equipe?competencia=${comp}`),
  });

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        {[0, 1, 2].map((i) => (
          <Esqueleto key={i} className="h-20" />
        ))}
      </div>
    );
  }

  const membros = data?.membros ?? [];
  const semVinculo = membros.filter((m) => !m.codigoVoluntario).length;

  if (membros.length === 0) {
    return (
      <Card className="p-2">
        <EstadoVazio
          icone={<Users size={24} />}
          titulo="Nenhum consultor cadastrado"
          descricao="Cadastre a equipe e vincule cada pessoa ao código de voluntário do SGA para o ranking funcionar."
        />
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="text-[13px] text-[var(--muted)]">Equipe</p>
        <h2 className="text-[26px] font-semibold capitalize text-[var(--fg)]">
          {competencia(comp)}
        </h2>
      </div>

      {semVinculo > 0 && (
        <Card className="flex items-start gap-3 p-4">
          <AlertTriangle size={18} className="mt-0.5 shrink-0 text-[var(--warn)]" />
          <p className="text-sm text-[var(--muted)]">
            <strong className="text-[var(--fg)]">
              {semVinculo} {semVinculo === 1 ? 'pessoa' : 'pessoas'} sem vínculo com o SGA.
            </strong>{' '}
            Sem o código de voluntário, as placas vendidas não são atribuídas a ninguém e não entram
            na apuração de bônus.
          </p>
        </Card>
      )}

      <ul className="flex list-none flex-col gap-3 p-0">
        {membros.map((m) => (
          <Card as="li" key={m.id} className="flex items-center gap-4 p-4">
            <div className="grid size-9 shrink-0 place-items-center rounded-full border border-[var(--border)] bg-[var(--bg-subtle)] text-[10px] font-medium text-[var(--muted)]">
              {iniciais(m.nome)}
            </div>

            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-[var(--fg)]">{m.nome}</p>
              <p className="truncate text-xs capitalize text-[var(--faint)]">
                {m.papel.replace('_', ' ')}
                {m.regional && ` · ${m.regional}`}
              </p>
            </div>

            {m.codigoVoluntario ? (
              <div className="shrink-0 text-right">
                <p className="num text-lg font-semibold text-[var(--fg)]">
                  {numero(m.placasLiquidas)}
                </p>
                <p className="text-xs text-[var(--faint)]">
                  {m.metaPlacas ? `de ${numero(m.metaPlacas)}` : 'placas'}
                </p>
              </div>
            ) : (
              <span
                title="Sem código de voluntário do SGA"
                className="flex shrink-0 items-center gap-1 text-xs font-semibold text-[var(--warn)]"
              >
                <Link2Off size={14} /> sem vínculo
              </span>
            )}
          </Card>
        ))}
      </ul>
    </div>
  );
}
